import {
    autoUpdate,
    flip,
    offset,
    Placement,
    shift,
    useDismiss,
    useFloating,
    useFocus,
    useHover,
    useInteractions,
    useRole,
} from '@floating-ui/react';
import { cloneElement, useMemo, useState } from 'react';
import { mergeRefs } from 'react-merge-refs';

type TooltipProps = {
    label?: string;
    placement?: Placement;
    children: JSX.Element;
};

export const Tooltip: FC<TooltipProps> = ({ children, label, placement = 'top' }) => {
    const [open, setOpen] = useState(false);

    const { x, y, refs, strategy, context } = useFloating({
        placement,
        open,
        onOpenChange: setOpen,
        middleware: [offset(5), flip(), shift({ padding: 8 })],
        whileElementsMounted: autoUpdate,
    });

    const { getReferenceProps, getFloatingProps } = useInteractions([
        useHover(context),
        useFocus(context),
        useRole(context, { role: 'tooltip' }),
        useDismiss(context),
    ]);

    const ref = useMemo(() => mergeRefs([refs.setReference, (children as any).ref]), [refs.setReference, children]);

    return (
        <>
            {cloneElement(children, getReferenceProps({ ref, ...children.props }))}
            {open && label !== undefined && (
                <div className="relative">
                    <div
                        {...getFloatingProps({
                            ref: refs.setFloating,
                            className: 'tooltip p-2 block bg-black opacity-75 rounded-md text-white',
                            style: {
                                position: strategy,
                                top: y,
                                left: x,
                            },
                        })}
                    >
                        {label}
                    </div>
                </div>
            )}
        </>
    );
};
